import { defineStore } from 'pinia';
import {
	GetTheIdoInstancesQuery,
	GetTheIdoListviewIdosInInstanceCountQuery,
	GetTheIdoListviewModelAndDataQuery,
	GetTheIdoListviewSummaryForAnInstanceQuery,
	GetTheSharedQueriesForTheKanbanBoardQuery,
	createIdoListCompleteExportQuery,
} from 'src/stores/idoList/queries';
import {
	GROUPED_IOD_LIST_SPLITTER,
	IDO_INSTANCE_COLUMN_CONFIG_KEY,
	IDO_INSTANCE_VISIBLE_FIELD_LIST_KEY,
} from 'src/stores/mainLayout/localStorageKeys';

import {
	IdoInstance,
	IdoInstanceRunType,
	IdoInstanceSearchResult,
	IdoListCompleteExportCsvRunType,
	IdoListSummaryEntry,
	IdoListSummaryEntryRunType,
	IdoListViewModel,
	IdoListViewModelRunType,
	IdoSharedQueriesForTheKanbanBoard,
	IdoSharedQueriesForTheKanbanBoardRunType,
	IdosInInstanceCount,
	IdosInInstanceCountResultRunType,
	cloneIdoDetailsSearchGroup,
	createEmptyIdoDetailsSearchGroup,
	normalizeSerializedIdoDetailsSearch,
	normalizeSharedQuerySerializedIdoDetailsSearch,
	parseLegacyIdoDetailsSearch,
	serializeLegacyIdoDetailsSearch,
} from 'src/models/idoList';
import { IdoListViewColumn } from 'src/models/idoList/fieldTypes';

import { TabIdGenerator } from 'src/services/TabIdGenerator';

import {
	ExportTheIdoListViewAsCsvQueryCallBack,
	GetTheIdoInstancesCallBack,
	GetTheIdoListviewIdosInInstanceCountQueryCallBack,
	GetTheIdoListviewModelAndDataQueryCallBack,
	GetTheIdoListviewSummaryForAnInstanceQueryCallBack,
	GetTheSharedQueriesForTheKanbanBoardCallBack,
} from './callbacks';
import {
	ExtendedIdoListTab,
	FetchIdoInstanceListPayload,
	FetchIdoListPayload,
	FetchIdoListSummaryPayload,
	FetchIdosInInstanceCountListPayload,
	FetchSharedQueriesForTheKanbanBoardPayload,
	FetchTheCompleteIdoListExportPayload,
	GlobalSearchCounts,
	IdoColumnConfig,
	IdoListStoreState,
	LoadDetailsSearchForTabPayload,
	SetDetailsSearchDraftForTabPayload,
	SetIdoInstanceColumnConfigPayload,
	SetIdoListPayload,
	SetIdoListResultCountPayload,
	SetIdoTableHeightPayload,
	SetPaginationPayload,
	SetSearchTermForInstanceIdPayload,
	SetSharedQueryColumnConfigPayload,
	SetTabCalculationActivePayload,
	SetTabDataPayload,
	SetTabLoadingPayload,
	SetTabSummaryLoadingPayload,
	SetTabSummaryPayload,
	TabData,
	TrackIdoDetailFromTabPayload,
	UpdateIdoColumnConfigPayload,
	UpdateSharedQueryColumnConfigPayload,
} from './interfaces';
import { Pagination } from './types';

const DEFAULT_TAB_PAGINATION: Pagination = {
	page: 1,
	rowsNumber: 0,
	rowsPerPage: 20,
	sort: {
		direction: 'ASC',
		property: '_id',
	},
};

const createDefaultDetailsSearchDraft = () => createEmptyIdoDetailsSearchGroup();

const parseMapFromLocalStorage = <TValue>(key: string): Map<string, TValue> | null => {
	try {
		const stored = localStorage.getItem(key);
		if (stored === null) {
			return null;
		}
		const parsed = JSON.parse(stored) as unknown;
		if (!Array.isArray(parsed)) {
			return null;
		}
		return new Map<string, TValue>(parsed as Array<[string, TValue]>);
	} catch (error) {
		console.warn(`Failed to load ${key} from localStorage:`, error);
		return null;
	}
};

const parseBooleanLike = (value: unknown): boolean => {
	if (typeof value === 'boolean') {
		return value;
	}
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		return normalized === '1' || normalized === 'true';
	}
	return false;
};

const createDefaultState = (): IdoListStoreState => ({
	activeIdoAggregateId: null,
	activeTabId: null,
	allInstancesSearchTerm: '',
	globalSearchCounts: [],
	globalSearchExplicitlyCleared: false,
	globalSearchFocusedInstanceId: null,
	groupedIdoListSplitter:
		localStorage.getItem(GROUPED_IOD_LIST_SPLITTER) !== null
			? parseInt(localStorage.getItem(GROUPED_IOD_LIST_SPLITTER) as string)
			: 250,
	idoInstanceColumnConfig: parseMapFromLocalStorage<IdoColumnConfig[]>(IDO_INSTANCE_COLUMN_CONFIG_KEY),
	idoInstanceFieldList: new Map<string, IdoListViewColumn[]>(),
	idoInstanceList: [],
	idoInstanceListLoaded: false,
	idoInstanceVisibleFieldList: parseMapFromLocalStorage<string[]>(IDO_INSTANCE_VISIBLE_FIELD_LIST_KEY),
	idoList: new Map<string, IdoInstanceSearchResult>(),
	idoListQueryIdentifierByTarget: new Map<string, string>(),
	idoListResultCount: new Map<string, number>(),
	idoListScrollPos: 0,
	idoSelectedInstance: null,
	idoSelectedSharedQuery: null,
	idoSharedQueriesForTheKanbanBoard: [],
	idoSharedQueryColumnConfig: parseMapFromLocalStorage<IdoColumnConfig[]>('IDO_SHARED_QUERY_COLUMN_CONFIG'),
	idoTableHeight: new Map<string, number>(),
	idosInInstanceCount: [],
	instanceCountFetchInFlightCount: 0,
	instanceCountFetchInProgress: false,
	pagination: new Map<string, Pagination>(),
	searchInProgress: false,
	showInstanceSelector: false,
	tabCalculationIsActive: new Map<string, boolean>(),
	tabData: new Map<string, TabData>(),
	tabSummary: new Map<string, IdoListSummaryEntry[]>(),
	tabSummaryLoading: new Map<string, boolean>(),
	tabToIdoDetailsMap: new Map<string, Set<string>>(),
	tabs: [],
});

const resolveTargetTabId = (
	store: IdoListStoreState,
	idoInstanceId: string,
	searchTerm: string,
	targetTabId?: string
): null | string => {
	if (targetTabId !== undefined) {
		return targetTabId;
	}
	if (idoInstanceId === 'all') {
		return null;
	}

	const expectedInstanceTabId = TabIdGenerator.generateInstanceTabId(idoInstanceId, searchTerm);
	const matchingTab = store.tabs.find((tab) => {
		if (tab.id === expectedInstanceTabId) {
			return true;
		}

		if (tab.type === 'sharedQuery' && tab.sharedQuery?.idoInstanceId === idoInstanceId) {
			const expectedSharedQueryTabId = TabIdGenerator.generateSharedQueryTabId(
				idoInstanceId,
				tab.sharedQuery.value,
				searchTerm
			);
			return tab.id === expectedSharedQueryTabId;
		}

		return tab.type === 'instance' && tab.instance?.ID === idoInstanceId && tab.searchTerm === searchTerm;
	});

	return matchingTab?.id ?? null;
};

const idoListQueryTargetKey = (idoInstanceId: string, targetTabId: null | string): string => {
	return targetTabId !== null ? `tab:${targetTabId}` : `instance:${idoInstanceId}`;
};

export const useIdoListStore = defineStore('idoList', {
	actions: {
		addTab(tab: ExtendedIdoListTab): void {
			const tabIndex = this.tabs.findIndex((existingTab) => existingTab.id === tab.id);
			this.tabs.forEach((existingTab) => {
				existingTab.isActive = false;
			});

			if (tabIndex === -1) {
				const newTab = { ...tab, isActive: Boolean(tab.isActive) };
				this.tabs.push(newTab);
				if (newTab.isActive) {
					this.activeTabId = newTab.id;
				}
			} else {
				this.tabs[tabIndex] = { ...tab, isActive: Boolean(tab.isActive) };
				if (tab.isActive) {
					this.activeTabId = tab.id;
				}
			}

			if (!this.tabs.some((existingTab) => existingTab.isActive)) {
				this.activeTabId = null;
			}

			this.showInstanceSelector = false;
		},
		applyDetailsSearchForTab(tabId: string): void {
			const existingData = this.tabData.get(tabId);
			if (existingData === undefined) {
				return;
			}

			const serializedDetailsSearch = normalizeSerializedIdoDetailsSearch(
				serializeLegacyIdoDetailsSearch(existingData.detailsSearchDraft)
			);
			this.tabData.set(tabId, {
				...existingData,
				serializedDetailsSearch,
			});
		},
		clearGlobalSearchResults(): void {
			try {
				this.idoListQueryIdentifierByTarget.delete(idoListQueryTargetKey('all', null));
				this.idoList.delete('all');
				this.idoListResultCount.delete('all');
				this.globalSearchCounts = [];
				this.globalSearchFocusedInstanceId = null;
			} catch {
				/* empty */
			}
		},
		clearGlobalSearchTabs(): void {
			const removedInstanceTabIds = this.tabs
				.filter(
					(tab) =>
						tab.type === 'instance' &&
						(tab.searchTerm ?? '').trim() !== '' &&
						(this.tabToIdoDetailsMap.get(tab.id)?.size ?? 0) === 0
				)
				.map((tab) => tab.id);
			this.tabs = this.tabs.filter((tab) => !removedInstanceTabIds.includes(tab.id));
			this.globalSearchCounts = [];
			this.allInstancesSearchTerm = '';
			this.globalSearchFocusedInstanceId = null;
			removedInstanceTabIds.forEach((tabId) => {
				this.idoListQueryIdentifierByTarget.delete(idoListQueryTargetKey('', tabId));
				this.tabData.delete(tabId);
				this.tabCalculationIsActive.delete(tabId);
				this.tabSummary.delete(tabId);
				this.tabSummaryLoading.delete(tabId);
				this.tabToIdoDetailsMap.delete(tabId);
			});

			if (this.tabs.length === 0) {
				this.activeTabId = null;
				this.showInstanceSelector = true;
				return;
			}

			this.tabs[0].isActive = true;
			this.activeTabId = this.tabs[0].id;
		},
		clearGlobalSearchTerm(): void {
			this.allInstancesSearchTerm = '';
		},
		clearTabData(tabId: string): void {
			this.idoListQueryIdentifierByTarget.delete(idoListQueryTargetKey('', tabId));
			this.tabData.delete(tabId);
		},
		clearTabs(): void {
			this.tabs = [];
			this.activeTabId = null;
			this.globalSearchFocusedInstanceId = null;
			this.showInstanceSelector = true;
			this.tabData.clear();
			this.tabCalculationIsActive.clear();
			this.tabSummary.clear();
			this.tabSummaryLoading.clear();
		},
		async fetchIdoInstanceList(payload: FetchIdoInstanceListPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setIdoInstanceList(null);
				return;
			}

			await payload.queryStore.query<IdoInstance[]>({
				callback: new GetTheIdoInstancesCallBack(this),
				createTime: new Date(),
				query: new GetTheIdoInstancesQuery(),
				responseType: 'json',
				runType: IdoInstanceRunType,
			});
		},
		async fetchIdoList(payload: FetchIdoListPayload): Promise<void> {
			const targetTabId = resolveTargetTabId(
				this,
				payload.idoInstanceId,
				payload.searchTerm,
				payload.targetTabId
			);

			if (payload.queryStore === undefined) {
				this.setIdoList({
					idoInstanceId: payload.idoInstanceId,
					idoListViewModel: null,
				});
				if (targetTabId !== null) {
					this.setTabSummary({
						summary: [],
						tabId: targetTabId,
					});
				}
				return;
			}

			const query = new GetTheIdoListviewModelAndDataQuery(
				payload.searchTerm,
				payload.idoInstanceId,
				payload.serializeIdoIdoListFilter,
				payload.page,
				payload.limit,
				payload.sort
			);
			const queryIdentifier = query.uniqueQueryIdentifier();
			this.setActiveIdoListQueryIdentifier({
				idoInstanceId: payload.idoInstanceId,
				queryIdentifier,
				targetTabId,
			});

			if (targetTabId !== null) {
				this.setTabLoading({ isLoading: true, tabId: targetTabId });
			}

			try {
				await payload.queryStore.query<IdoListViewModel>({
					callback: new GetTheIdoListviewModelAndDataQueryCallBack(
						this,
						payload.idoInstanceId,
						payload.searchTerm,
						targetTabId,
						payload.page,
						payload.sort,
						payload.limit,
						queryIdentifier
					),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: IdoListViewModelRunType,
				});
			} finally {
				if (targetTabId !== null) {
					this.setTabLoading({ isLoading: false, tabId: targetTabId });
				}
			}

			if (targetTabId !== null) {
				if (this.calculationActiveForTab(targetTabId)) {
					await this.fetchIdoListSummary({
						idoInstanceId: payload.idoInstanceId,
						queryStore: payload.queryStore,
						searchTerm: payload.searchTerm,
						serializeIdoIdoListFilter: payload.serializeIdoIdoListFilter,
						targetTabId: targetTabId,
					});
				} else {
					this.setTabSummary({
						summary: [],
						tabId: targetTabId,
					});
					this.setTabSummaryLoading({
						isLoading: false,
						tabId: targetTabId,
					});
				}
			}
		},
		async fetchIdoListSummary(payload: FetchIdoListSummaryPayload): Promise<void> {
			if (payload.targetTabId === '') {
				return;
			}

			this.setTabSummaryLoading({
				isLoading: true,
				tabId: payload.targetTabId,
			});

			if (payload.queryStore === undefined) {
				this.setTabSummary({
					summary: [],
					tabId: payload.targetTabId,
				});
				this.setTabSummaryLoading({
					isLoading: false,
					tabId: payload.targetTabId,
				});
				return;
			}

			await payload.queryStore.query<IdoListSummaryEntry[]>({
				callback: new GetTheIdoListviewSummaryForAnInstanceQueryCallBack(this, payload.targetTabId),
				createTime: new Date(),
				query: new GetTheIdoListviewSummaryForAnInstanceQuery(
					payload.searchTerm,
					payload.idoInstanceId,
					payload.serializeIdoIdoListFilter
				),
				responseType: 'json',
				runType: IdoListSummaryEntryRunType,
			});
		},
		async fetchIdosInInstanceCountList(payload: FetchIdosInInstanceCountListPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setIdosInInstanceCountList([]);
				this.setInstanceCountFetchInProgress(false);
				return;
			}

			this.setInstanceCountFetchInProgress(true);

			try {
				await payload.queryStore.query<IdosInInstanceCount[]>({
					callback: new GetTheIdoListviewIdosInInstanceCountQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheIdoListviewIdosInInstanceCountQuery(),
					responseType: 'json',
					runType: IdosInInstanceCountResultRunType,
				});
			} finally {
				this.setInstanceCountFetchInProgress(false);
			}
		},
		async fetchSharedQueriesForTheKanbanBoard(payload: FetchSharedQueriesForTheKanbanBoardPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setSharedQueriesForTheKanbanBoard(null);
				return;
			}

			await payload.queryStore.query<IdoSharedQueriesForTheKanbanBoard[]>({
				callback: new GetTheSharedQueriesForTheKanbanBoardCallBack(this),
				createTime: new Date(),
				query: new GetTheSharedQueriesForTheKanbanBoardQuery(),
				responseType: 'json',
				runType: IdoSharedQueriesForTheKanbanBoardRunType,
			});
		},
		async fetchTheCompleteIdoListExportAsCsv(payload: FetchTheCompleteIdoListExportPayload): Promise<Blob | null> {
			if (payload.queryStore === undefined) {
				return null;
			}

			let csvBlob: Blob | null = null;

			await payload.queryStore.query<Blob>({
				callback: new ExportTheIdoListViewAsCsvQueryCallBack((exportedCsvBlob) => {
					csvBlob = exportedCsvBlob;
				}),
				createTime: new Date(),
				query: createIdoListCompleteExportQuery(
					payload.exportType,
					payload.searchTerm,
					payload.idoInstanceId,
					payload.serializeIdoIdoListFilter,
					payload.idoInstanceName
				),
				responseType: 'blob',
				runType: IdoListCompleteExportCsvRunType,
			});

			if (csvBlob !== null && !(csvBlob as Blob).type.includes('text/csv')) {
				return null;
			}

			return csvBlob;
		},
		insertInstanceTabAtStart(newTab: {
			id: string;
			instance: IdoInstance;
			label: string;
			searchTerm: string;
		}): void {
			this.tabs.forEach((t) => {
				t.isActive = false;
			});
			this.tabs.unshift({
				id: newTab.id,
				instance: newTab.instance,
				isActive: true,
				label: newTab.label,
				searchTerm: newTab.searchTerm,
				type: 'instance',
			});
			this.activeTabId = newTab.id;
			this.showInstanceSelector = false;
		},
		isLatestIdoListQueryIdentifier(payload: {
			idoInstanceId: string;
			queryIdentifier: string;
			targetTabId: null | string;
		}): boolean {
			const targetKey = idoListQueryTargetKey(payload.idoInstanceId, payload.targetTabId);
			return this.idoListQueryIdentifierByTarget.get(targetKey) === payload.queryIdentifier;
		},
		loadDetailsSearchForTab(payload: LoadDetailsSearchForTabPayload): void {
			const existingData = this.tabData.get(payload.tabId);

			this.tabData.set(payload.tabId, {
				data: existingData?.data,
				detailsSearchDraft: parseLegacyIdoDetailsSearch(payload.serializedDetailsSearch),
				isLoading: existingData?.isLoading ?? false,
				lastSearchTerm: existingData?.lastSearchTerm ?? '',
				pagination: existingData?.pagination ?? { ...DEFAULT_TAB_PAGINATION },
				resultCount: existingData?.resultCount ?? 0,
				serializedDetailsSearch: payload.serializedDetailsSearch,
			});
		},
		removeTab(tabId: string): void {
			const tabIndex = this.tabs.findIndex((tab) => tab.id === tabId);
			if (tabIndex === -1) {
				return;
			}

			const wasActive = this.tabs[tabIndex].isActive;
			this.tabs.splice(tabIndex, 1);
			this.idoListQueryIdentifierByTarget.delete(idoListQueryTargetKey('', tabId));
			this.tabData.delete(tabId);
			this.tabCalculationIsActive.delete(tabId);
			this.tabSummary.delete(tabId);
			this.tabSummaryLoading.delete(tabId);
			this.tabToIdoDetailsMap.delete(tabId);

			if (wasActive && this.tabs.length > 0) {
				const newActiveIndex = tabIndex < this.tabs.length ? tabIndex : tabIndex - 1;
				if (newActiveIndex >= 0) {
					this.tabs[newActiveIndex].isActive = true;
					this.activeTabId = this.tabs[newActiveIndex].id;
				}
				return;
			}

			if (this.tabs.length === 0) {
				this.activeTabId = null;
				this.idoSelectedInstance = null;
				this.idoSelectedSharedQuery = null;
				this.globalSearchFocusedInstanceId = null;
				this.showInstanceSelector = true;
			}
		},
		reorderTab(fromIndex: number, toIndex: number): void {
			if (
				fromIndex < 0 ||
				toIndex < 0 ||
				fromIndex >= this.tabs.length ||
				toIndex >= this.tabs.length ||
				fromIndex === toIndex
			) {
				return;
			}
			const reordered: ExtendedIdoListTab[] = [...this.tabs];
			const movedTabs = reordered.splice(fromIndex, 1);
			reordered.splice(toIndex, 0, ...movedTabs);
			this.tabs = reordered;
		},
		replaceInstanceTab(
			oldTabId: string,
			newTab: { id: string; instance: IdoInstance; label: string; searchTerm: string }
		): void {
			const index = this.tabs.findIndex((t) => t.id === oldTabId);
			if (index === -1) return;

			this.tabData.delete(oldTabId);
			this.tabCalculationIsActive.delete(oldTabId);
			this.tabSummary.delete(oldTabId);
			this.tabSummaryLoading.delete(oldTabId);

			this.tabs.forEach((t) => {
				t.isActive = false;
			});
			this.tabs[index] = {
				id: newTab.id,
				instance: newTab.instance,
				isActive: true,
				label: newTab.label,
				searchTerm: newTab.searchTerm,
				type: 'instance',
			};
			this.activeTabId = newTab.id;
		},
		reset(): void {
			Object.assign(this, createDefaultState());
			this.showInstanceSelector = true;
		},
		resetDetailsSearchForTab(tabId: string): void {
			const existingData = this.tabData.get(tabId);
			if (existingData === undefined) {
				return;
			}

			this.tabData.set(tabId, {
				...existingData,
				detailsSearchDraft: createDefaultDetailsSearchDraft(),
				serializedDetailsSearch: '',
			});
		},
		setActiveIdoAggregateId(aggregateId: null | string): void {
			this.activeIdoAggregateId = aggregateId;
		},
		setActiveIdoListQueryIdentifier(payload: {
			idoInstanceId: string;
			queryIdentifier: string;
			targetTabId: null | string;
		}): void {
			const targetKey = idoListQueryTargetKey(payload.idoInstanceId, payload.targetTabId);
			this.idoListQueryIdentifierByTarget.set(targetKey, payload.queryIdentifier);
		},
		setActiveTab(tabId: null | string): void {
			this.tabs.forEach((tab) => {
				tab.isActive = tab.id === tabId;
			});
			this.activeTabId = tabId;
			this.showInstanceSelector = this.tabs.length === 0;
		},
		setDetailsSearchDraftForTab(payload: SetDetailsSearchDraftForTabPayload): void {
			const existingData = this.tabData.get(payload.tabId);
			if (existingData === undefined) {
				this.tabData.set(payload.tabId, {
					data: undefined,
					detailsSearchDraft: cloneIdoDetailsSearchGroup(payload.detailsSearchDraft),
					isLoading: false,
					lastSearchTerm: '',
					pagination: { ...DEFAULT_TAB_PAGINATION },
					resultCount: 0,
					serializedDetailsSearch: '',
				});
				return;
			}

			this.tabData.set(payload.tabId, {
				...existingData,
				detailsSearchDraft: cloneIdoDetailsSearchGroup(payload.detailsSearchDraft),
			});
		},
		setGlobalSearchCounts(counts: Array<GlobalSearchCounts>): void {
			this.globalSearchCounts = counts;
		},
		setGlobalSearchExplicitlyCleared(cleared: boolean): void {
			this.globalSearchExplicitlyCleared = cleared;
		},
		setGlobalSearchFocusedInstanceId(instanceId: null | string): void {
			this.globalSearchFocusedInstanceId = instanceId;
			this.showInstanceSelector = !instanceId;
		},
		setGlobalSearchTerm(searchTerm: string): void {
			this.allInstancesSearchTerm = searchTerm;
		},
		setGroupedIdoListSplitter(splitterPosition: number): void {
			this.groupedIdoListSplitter = splitterPosition;
			localStorage.setItem(GROUPED_IOD_LIST_SPLITTER, String(splitterPosition));
		},
		setIdoInstanceColumnConfig(payload: SetIdoInstanceColumnConfigPayload): void {
			if (this.idoInstanceColumnConfig === null) {
				this.idoInstanceColumnConfig = new Map<string, IdoColumnConfig[]>();
			}
			this.idoInstanceColumnConfig.set(payload.instanceId, payload.columnConfig);
			localStorage.setItem(
				IDO_INSTANCE_COLUMN_CONFIG_KEY,
				JSON.stringify(Array.from(this.idoInstanceColumnConfig.entries()))
			);

			if (this.idoInstanceVisibleFieldList === null) {
				this.idoInstanceVisibleFieldList = new Map<string, string[]>();
			}
			const visibleFields = payload.columnConfig
				.filter((column) => column.visible)
				.sort((a, b) => a.sortOrder - b.sortOrder)
				.map((column) => column.dataIndex);
			this.idoInstanceVisibleFieldList.set(payload.instanceId, visibleFields);
			localStorage.setItem(
				IDO_INSTANCE_VISIBLE_FIELD_LIST_KEY,
				JSON.stringify(Array.from(this.idoInstanceVisibleFieldList.entries()))
			);
		},
		setIdoInstanceFieldList(idoInstanceFieldList: Map<string, IdoListViewColumn[]>): void {
			this.idoInstanceFieldList = idoInstanceFieldList;
			const currentVisible =
				this.idoInstanceVisibleFieldList !== null
					? new Map<string, string[]>(this.idoInstanceVisibleFieldList)
					: new Map<string, string[]>();

			idoInstanceFieldList.forEach((columns, instanceId) => {
				const allServerFields = new Set((columns || []).map((column) => column.dataIndex));
				const serverDefaultVisible = (columns || [])
					.filter((column) => column.hidden !== 'true' || !('hidden' in column))
					.map((column) => column.dataIndex);
				const userPreferences = currentVisible.get(instanceId);

				let finalVisible: string[] = [];
				if (userPreferences && userPreferences.length > 0) {
					const validUserFields = userPreferences.filter((field) => allServerFields.has(field));
					finalVisible = validUserFields.length > 0 ? validUserFields : serverDefaultVisible;
				} else {
					finalVisible = serverDefaultVisible;
				}

				if (finalVisible.length === 0) {
					finalVisible = [
						'ID',
						'Name',
						'Description',
						'Status',
						'CreatedBy',
						'CreatedAt',
						'ModifiedBy',
						'ModifiedAt',
					];
				}

				currentVisible.set(instanceId, finalVisible);
			});

			this.idoInstanceVisibleFieldList = currentVisible;
			localStorage.setItem(
				IDO_INSTANCE_VISIBLE_FIELD_LIST_KEY,
				JSON.stringify(Array.from(currentVisible.entries()))
			);
		},
		setIdoInstanceList(idoInstanceList: IdoInstance[] | null): void {
			this.idoInstanceList = Array.isArray(idoInstanceList) ? idoInstanceList : [];
			this.idoInstanceListLoaded = Array.isArray(idoInstanceList);
		},
		setIdoInstanceVisibleFieldList(idoInstanceVisibleFieldList: Map<string, string[]>): void {
			this.idoInstanceVisibleFieldList = idoInstanceVisibleFieldList;
			localStorage.setItem(
				IDO_INSTANCE_VISIBLE_FIELD_LIST_KEY,
				JSON.stringify(Array.from(idoInstanceVisibleFieldList.entries()))
			);
		},
		setIdoList(payload: SetIdoListPayload): void {
			this.idoList.set(payload.idoInstanceId, payload.idoListViewModel);
		},
		setIdoListResultCount(payload: SetIdoListResultCountPayload): void {
			this.idoListResultCount.set(payload.idoInstanceId, payload.idoListResultCount);
		},
		setIdoListScrollPos(scrollPos: number): void {
			this.idoListScrollPos = scrollPos;
		},
		setIdoSelectedInstance(idoSelectedInstance: IdoInstance | null): void {
			this.idoSelectedInstance = idoSelectedInstance;
		},
		setIdoSelectedSharedQuery(idoSelectedSharedQuery: IdoSharedQueriesForTheKanbanBoard | null): void {
			this.idoSelectedSharedQuery = idoSelectedSharedQuery;
		},
		setIdoTableHeight(payload: SetIdoTableHeightPayload): void {
			this.idoTableHeight.set(payload.idoInstanceId, payload.idoTableHeight);
		},
		setIdosInInstanceCountList(idosInInstanceCount: Array<IdosInInstanceCount>): void {
			this.idosInInstanceCount = idosInInstanceCount;
		},
		setInstanceCountFetchInProgress(instanceCountFetchInProgress: boolean): void {
			// Zählend statt boolesch: GetTheIdoListviewIdosInInstanceCountQuery hat keine
			// Konstruktor-Argumente, ihr uniqueQueryIdentifier() ist also bei jedem Aufruf
			// byte-identisch — der QueryStore bricht bei Überlappung die erste Query ab, die dann
			// still (ohne Callback) auflöst. Ein einzelner Boolean würde dabei fälschlich auf
			// `false` fallen, während die zweite, wirklich noch laufende Anfrage weiterläuft.
			this.instanceCountFetchInFlightCount = instanceCountFetchInProgress
				? this.instanceCountFetchInFlightCount + 1
				: Math.max(0, this.instanceCountFetchInFlightCount - 1);
			this.instanceCountFetchInProgress = this.instanceCountFetchInFlightCount > 0;
		},
		setPagination(payload: SetPaginationPayload): void {
			this.pagination.set(payload.idoInstanceId, payload.pagination);
		},
		setSearchInProgress(payload: { searchInProgress: boolean } | boolean): void {
			this.searchInProgress = typeof payload === 'boolean' ? payload : payload.searchInProgress;
		},
		setSearchTermForInstanceId(payload: SetSearchTermForInstanceIdPayload): void {
			if (payload.idoInstanceId === 'all') {
				this.allInstancesSearchTerm = payload.searchTerm;
			}
		},
		setSharedQueriesForTheKanbanBoard(sharedQueries: IdoSharedQueriesForTheKanbanBoard[] | null): void {
			this.idoSharedQueriesForTheKanbanBoard = Array.isArray(sharedQueries) ? sharedQueries : [];
		},
		setSharedQueryColumnConfig(payload: SetSharedQueryColumnConfigPayload): void {
			if (this.idoSharedQueryColumnConfig === null) {
				this.idoSharedQueryColumnConfig = new Map<string, IdoColumnConfig[]>();
			}

			this.idoSharedQueryColumnConfig.set(payload.sharedQueryId, payload.columnConfig);
			localStorage.setItem(
				'IDO_SHARED_QUERY_COLUMN_CONFIG',
				JSON.stringify(Array.from(this.idoSharedQueryColumnConfig.entries()))
			);
		},
		setShowInstanceSelector(showInstanceSelector: boolean): void {
			this.showInstanceSelector = showInstanceSelector;
		},
		setTabCalculationActive(payload: SetTabCalculationActivePayload): void {
			this.tabCalculationIsActive.set(payload.tabId, payload.isActive);
		},
		setTabData(payload: SetTabDataPayload): void {
			const existingData = this.tabData.get(payload.tabId);
			this.tabData.set(payload.tabId, {
				data: payload.data,
				detailsSearchDraft:
					payload.detailsSearchDraft !== undefined
						? cloneIdoDetailsSearchGroup(payload.detailsSearchDraft)
						: (existingData?.detailsSearchDraft ?? createDefaultDetailsSearchDraft()),
				isLoading: false,
				lastSearchTerm: payload.searchTerm ?? payload.lastSearchTerm ?? '',
				pagination: payload.pagination,
				resultCount: payload.resultCount ?? payload.data?.totalCount ?? payload.pagination.rowsNumber,
				serializedDetailsSearch: payload.serializedDetailsSearch ?? existingData?.serializedDetailsSearch ?? '',
			});
		},
		setTabLoading(payload: SetTabLoadingPayload): void {
			const existingData = this.tabData.get(payload.tabId);
			if (existingData) {
				this.tabData.set(payload.tabId, {
					...existingData,
					isLoading: payload.isLoading,
				});
				return;
			}

			this.tabData.set(payload.tabId, {
				data: undefined,
				detailsSearchDraft: createDefaultDetailsSearchDraft(),
				isLoading: payload.isLoading,
				lastSearchTerm: '',
				pagination: { ...DEFAULT_TAB_PAGINATION },
				resultCount: 0,
				serializedDetailsSearch: '',
			});
		},
		setTabSummary(payload: SetTabSummaryPayload): void {
			this.tabSummary.set(payload.tabId, payload.summary);
		},
		setTabSummaryLoading(payload: SetTabSummaryLoadingPayload): void {
			this.tabSummaryLoading.set(payload.tabId, payload.isLoading);
		},
		trackIdoDetailFromTab(payload: TrackIdoDetailFromTabPayload): void {
			if (!this.tabToIdoDetailsMap.has(payload.tabId)) {
				this.tabToIdoDetailsMap.set(payload.tabId, new Set<string>());
			}
			this.tabToIdoDetailsMap.get(payload.tabId)?.add(payload.idoDetailId);
		},
		untrackIdoDetailFromTab(payload: TrackIdoDetailFromTabPayload): void {
			const details = this.tabToIdoDetailsMap.get(payload.tabId);
			if (details === undefined) {
				return;
			}
			details.delete(payload.idoDetailId);
			if (details.size === 0) {
				this.tabToIdoDetailsMap.delete(payload.tabId);
			}
		},
		updateIdoColumnConfig(payload: UpdateIdoColumnConfigPayload): void {
			this.setIdoInstanceColumnConfig(payload);
		},
		updateSharedQueryColumnConfig(payload: UpdateSharedQueryColumnConfigPayload): void {
			this.setSharedQueryColumnConfig(payload);
		},
	},
	getters: {
		activeTab(state): ExtendedIdoListTab | null {
			if (state.activeTabId === null) {
				return null;
			}
			return state.tabs.find((tab) => tab.id === state.activeTabId) ?? null;
		},
		calculationActiveForTab(state): (tabId: string) => boolean {
			return (tabId: string): boolean => {
				if (state.tabCalculationIsActive.has(tabId)) {
					return state.tabCalculationIsActive.get(tabId) === true;
				}

				const tab = state.tabs.find((entry) => entry.id === tabId);
				if (tab?.type === 'sharedQuery' && tab.sharedQuery !== undefined) {
					return parseBooleanLike(tab.sharedQuery.idoIdoListToolbarCalculationIsActive);
				}

				return false;
			};
		},
		globalSearchTerm(state): string {
			return state.allInstancesSearchTerm;
		},
		hasActiveDetailsSearch(state): (tabId: string) => boolean {
			return (tabId: string): boolean => {
				const serializedDetailsSearch = state.tabData.get(tabId)?.serializedDetailsSearch ?? '';
				return normalizeSerializedIdoDetailsSearch(serializedDetailsSearch) !== '';
			};
		},
		hasOpenIdoDetailsFromTab(state): (tabId: string) => boolean {
			return (tabId: string): boolean => (state.tabToIdoDetailsMap.get(tabId)?.size ?? 0) > 0;
		},
		instanceTabs(state): ExtendedIdoListTab[] {
			return state.tabs;
		},
		normalModeSearchTerm(state): Map<string, string> {
			const searchMap = new Map<string, string>();
			state.tabs.forEach((tab) => {
				if (tab.type === 'instance' && tab.instance && tab.searchTerm) {
					searchMap.set(tab.instance.ID, tab.searchTerm);
				}
			});
			return searchMap;
		},
		searchTermForInstanceId(state): (instanceId: string) => string {
			return (instanceId: string): string => {
				if (instanceId === 'all') {
					return state.allInstancesSearchTerm;
				}
				return '';
			};
		},
		serializedDetailsSearchForTab(state): (tabId: string) => string {
			return (tabId: string): string => {
				const tabData = state.tabData.get(tabId);
				if (tabData !== undefined) {
					return tabData.serializedDetailsSearch;
				}

				const matchingTab = state.tabs.find((tab) => tab.id === tabId);
				if (matchingTab?.type === 'sharedQuery') {
					return normalizeSharedQuerySerializedIdoDetailsSearch(
						matchingTab.sharedQuery?.serializedIdoIdoListToolbarHiddenDetailsSearchString
					);
				}

				return '';
			};
		},
		tabDataById(state): (tabId: string) => TabData | undefined {
			return (tabId: string): TabData | undefined => state.tabData.get(tabId);
		},
		tabSummaryByTabId(state): (tabId: string) => IdoListSummaryEntry[] {
			return (tabId: string): IdoListSummaryEntry[] => state.tabSummary.get(tabId) ?? [];
		},
		tabSummaryLoadingByTabId(state): (tabId: string) => boolean {
			return (tabId: string): boolean => state.tabSummaryLoading.get(tabId) ?? false;
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
